<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import KlaviyoSubscriptionControls from 'dashboard/components-next/Contacts/Pages/KlaviyoSubscriptionControls.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const contactsFormRef = ref(null);
const formKey = ref(0);
const editableContactData = ref({});

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const isFormInvalid = computed(
  () => contactsFormRef.value?.isFormInvalid ?? false
);

const cloneContactData = contact => JSON.parse(JSON.stringify(contact || {}));

const resetState = () => {
  editableContactData.value = cloneContactData(props.selectedContact);
  formKey.value += 1;
};

const open = () => {
  resetState();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleDialogClose = () => {
  resetState();
};

const handleFormUpdate = updatedData => {
  editableContactData.value = {
    ...editableContactData.value,
    ...updatedData,
  };
};

const handleSubmit = async () => {
  try {
    const { customAttributes, ...basicContactData } = editableContactData.value;

    await store.dispatch('contacts/update', basicContactData);
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );

    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
    close();
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :show-cancel-button="false"
    :confirm-button-label="t('DIALOG.BUTTONS.CONFIRM')"
    :is-loading="isUpdating"
    :disable-confirm-button="isUpdating || isFormInvalid"
    dialog-class="max-w-none h-dvh max-h-dvh overflow-hidden rounded-none border-0 bg-transparent p-0 shadow-none sm:max-w-2xl sm:h-fit sm:max-h-[calc(100vh-4rem)] sm:overflow-visible"
    content-class="h-dvh min-h-dvh overflow-y-auto gap-5 rounded-none px-4 py-4 sm:h-fit sm:min-h-0 sm:max-h-[calc(100vh-4rem)] sm:rounded-xl sm:p-6"
    @confirm="handleSubmit"
    @close="handleDialogClose"
  >
    <template #header>
      <div class="grid items-center grid-cols-[2.5rem_1fr_2.5rem]">
        <Button
          ghost
          slate
          sm
          icon="i-lucide-x"
          type="button"
          class="!rounded-full bg-n-alpha-black2"
          :aria-label="t('GENERAL.CLOSE')"
          @click="close"
        />
        <h3 class="text-base font-medium leading-6 text-center text-n-slate-12">
          {{ t('EDIT_CONTACT.TITLE') }}
        </h3>
        <div aria-hidden="true" />
      </div>
    </template>

    <div class="flex flex-col">
      <ContactsForm
        :key="formKey"
        ref="contactsFormRef"
        :contact-data="editableContactData"
        is-details-view
        :show-edit-section-title="false"
        @update="handleFormUpdate"
      />
      <div class="mt-6 pt-5 border-t border-n-weak">
        <KlaviyoSubscriptionControls :selected-contact="selectedContact" />
      </div>
    </div>
  </Dialog>
</template>
